import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SolutionsAppService } from 'src/app/services/solutions-app.service';

interface RunRow {
  id: string;
  name: string;
  scheduled: boolean;
  onUpload: boolean;
  onDelete: boolean;
  checked: boolean;
}

/**
 * Pick which projects of a subscribed version to run. Nothing is ticked on open.
 * Closes with the queued task id; closes with undefined when cancelled.
 */
@Component({
  selector: 'app-run-projects-dialog',
  template: `
    <div class="dlg" role="document">
      <div class="dlg-h">
        <h2 mat-dialog-title id="run-dlg-title">Run {{ sub['solution_name'] | textpretty | titlecase }}</h2>
        <p>Pick the projects to run. They run one after another, in the order shown.</p>
      </div>

      <div class="state" *ngIf="loading">
        <mat-spinner diameter="22"></mat-spinner> Loading projects…
      </div>
      <div class="state err" *ngIf="loadError">Could not load the projects. Close this and try again.</div>
      <div class="state" *ngIf="!loading && !loadError && !rows.length">No projects in this solution.</div>

      <ng-container *ngIf="!loading && !loadError && rows.length">
        <div class="tb">
          <button type="button" (click)="setAll(true)" [disabled]="submitting">Select all</button>
          <button type="button" (click)="setAll(false)" [disabled]="submitting">Clear</button>
          <span class="cnt" aria-live="polite">{{ selectedCount }} of {{ rows.length }} selected</span>
        </div>
        <div class="dlg-b">
          <label class="ck" *ngFor="let row of rows; let i = index" [class.on]="row.checked">
            <input type="checkbox" [(ngModel)]="row.checked" [disabled]="submitting" />
            <span class="o">{{ i + 1 }}</span>
            <span class="t">
              {{ row.name }}
              <span class="chip sched" *ngIf="row.scheduled">Scheduled</span>
              <span class="chip auto" *ngIf="row.onUpload">Starts on upload</span>
              <span class="chip auto" *ngIf="row.onDelete">Starts on file delete</span>
            </span>
          </label>
        </div>
      </ng-container>

      <div class="dlg-f">
        <span class="err" *ngIf="runError">Unable to start the run. Try again.</span>
        <span class="r">
          <button type="button" class="btn-ghost" (click)="cancel()" [disabled]="submitting">Cancel</button>
          <button
            *ngIf="!loadError"
            type="button"
            class="btn-primary"
            [disabled]="submitting || selectedCount === 0"
            (click)="run()"
          >
            <mat-spinner *ngIf="submitting" diameter="14" class="spin"></mat-spinner>
            <svg *ngIf="!submitting" viewBox="0 0 24 24" width="15" height="15" fill="currentColor" stroke="none" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
            {{ runLabel }}
          </button>
        </span>
      </div>
    </div>
  `,
  styleUrls: ['./run-projects-dialog.component.scss'],
})
export class RunProjectsDialogComponent implements OnInit {
  sub: any;
  rows: RunRow[] = [];
  loading = true;
  loadError = false;
  submitting = false;
  runError = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { sub: any },
    private dialogRef: MatDialogRef<RunProjectsDialogComponent, string>,
    private solutionServices: SolutionsAppService
  ) {
    this.sub = data.sub;
  }

  /** The order get_runnable_pipelines runs them in: execution_order_id, then name. */
  static inRunOrder(projects: any[]): any[] {
    return [...projects].sort(
      (a, b) =>
        (a?.execution_order_id ?? Number.MAX_SAFE_INTEGER) - (b?.execution_order_id ?? Number.MAX_SAFE_INTEGER) ||
        String(a?.project_name ?? '').localeCompare(String(b?.project_name ?? ''))
    );
  }

  ngOnInit(): void {
    // Schedules and triggers only label rows; if either fails the dialog still runs.
    forkJoin({
      projects: this.solutionServices.getAvailableProjects(this.sub, this.sub, ''),
      schedules: this.solutionServices.getScheduledProject(this.sub, this.sub).pipe(catchError(() => of([]))),
      triggers: this.solutionServices
        .getVersionTriggers(this.sub['solution_id'], this.sub['version_id'])
        .pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ projects, schedules, triggers }: any) => {
        this.rows = this.buildRows(projects || [], schedules || [], triggers || []);
        this.loading = false;
      },
      error: (error: any) => {
        console.error('an error occurred loading projects', JSON.stringify(error));
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  private buildRows(projects: any[], schedules: any[], triggers: any[]): RunRow[] {
    const scheduled = new Set(
      (Array.isArray(schedules) ? schedules : [])
        .filter((t: any) => t?.enabled)
        .map((t: any) => String(t?.project_id))
    );
    const triggered = (method: string) =>
      new Set(
        (Array.isArray(triggers) ? triggers : [])
          .filter((t: any) => String(t?.api_method).toUpperCase() === method)
          .map((t: any) => String(t?.project?.project_id))
      );
    const onUpload = triggered('POST');
    const onDelete = triggered('DELETE');
    return RunProjectsDialogComponent.inRunOrder(projects).map((p: any) => {
      const id = String(p['project_id']);
      return {
        id,
        name: p['project_name'],
        scheduled: scheduled.has(id),
        onUpload: onUpload.has(id),
        onDelete: onDelete.has(id),
        checked: false,
      };
    });
  }

  get selectedCount(): number {
    return this.rows.filter((r) => r.checked).length;
  }

  get runLabel(): string {
    if (this.submitting) return 'Queuing…';
    const n = this.selectedCount;
    return n === 0 ? 'Run' : `Run ${n} project${n === 1 ? '' : 's'}`;
  }

  setAll(value: boolean): void {
    this.rows.forEach((r) => (r.checked = value));
  }

  cancel(): void {
    if (!this.submitting) this.dialogRef.close();
  }

  run(): void {
    if (this.submitting || this.selectedCount === 0) return;
    this.submitting = true;
    this.runError = false;
    this.dialogRef.disableClose = true;
    const projectIds = this.rows.filter((r) => r.checked).map((r) => r.id);
    this.solutionServices
      .runVersionOrProjectOrPipelineOrNode(this.sub['solution_id'], this.sub['version_id'], {
        project_ids: projectIds,
        pipeline_ids: [],
        node_ids: [],
        tags: [],
      })
      .subscribe({
        next: (response: any) => {
          this.dialogRef.disableClose = false;
          this.dialogRef.close(response?.task_id ?? '');
        },
        error: (error: any) => {
          console.error('an error occurred while running projects', JSON.stringify(error));
          this.submitting = false;
          this.runError = true;
          this.dialogRef.disableClose = false;
        },
      });
  }
}
